import { AddressFormKey } from '../../enums/addressFormKey';
import type { AddressSearchResult } from '../../models/services/api/getAddresses';

type PersonalDetailsFormData = Record<string, string | boolean | undefined>;

/**
 * Restores the mailing address `selectedSearchAddress` state on mount from
 * persisted store data.
 *
 * `selectedSearchAddress` is the only source of the vendor-only detail
 * fields (streetNumber, street, poBox, externalAVSAddressId) at submit time
 * - they're deliberately never stored in form state. Without this, that
 * state always starts as `null` on remount (e.g. navigating back to this
 * page from a later step and then continuing again without re-searching),
 * causing `validateAddressMatch` to report the address as invalid and
 * `buildAddressSubmissionFields` to wipe out those detail fields on
 * resubmit, even though nothing changed.
 *
 * Only restores when the address was previously recorded as valid - if it
 * wasn't, there's no trustworthy vendor data to restore, so `null` (the
 * previous default) is kept.
 */
export function restoreMailingSelectedAddress(
  personalDetails: PersonalDetailsFormData | undefined,
): AddressSearchResult | null {
  if (!personalDetails?.[AddressFormKey.IS_VALID]) {
    return null;
  }

  return {
    id: String(personalDetails[AddressFormKey.ADDRESS_ID] ?? ''),
    address: String(personalDetails[AddressFormKey.ADDRESS] ?? ''),
    addressLine1: String(personalDetails[AddressFormKey.ADDRESS_LINE_1] ?? ''),
    subBuilding: String(personalDetails[AddressFormKey.SUB_BUILDING] ?? ''),
    city: String(personalDetails[AddressFormKey.CITY] ?? ''),
    regionOrStateCode: String(personalDetails[AddressFormKey.STATE_CODE] ?? ''),
    postalCode: String(personalDetails[AddressFormKey.POSTAL_CODE] ?? ''),
    streetNumber: String(personalDetails[AddressFormKey.STREET_NUMBER] ?? ''),
    street: String(personalDetails[AddressFormKey.STREET] ?? ''),
    poBox: String(personalDetails[AddressFormKey.PO_BOX] ?? ''),
  };
}

/**
 * Restores the merchandise-location `selectedMerchandiseAddress` state on
 * mount from persisted store data. See `restoreMailingSelectedAddress` for
 * why this is needed.
 */
export function restoreMerchandiseSelectedAddress(
  personalDetails: PersonalDetailsFormData | undefined,
): AddressSearchResult | null {
  if (!personalDetails?.[AddressFormKey.MERCHANDISE_IS_VALID]) {
    return null;
  }

  return {
    id: String(personalDetails[AddressFormKey.MERCHANDISE_ADDRESS_ID] ?? ''),
    address: String(personalDetails[AddressFormKey.MERCHANDISE_ADDRESS] ?? ''),
    addressLine1: String(personalDetails[AddressFormKey.MERCHANDISE_ADDRESS_LINE_1] ?? ''),
    subBuilding: String(personalDetails[AddressFormKey.MERCHANDISE_SUB_BUILDING] ?? ''),
    city: String(personalDetails[AddressFormKey.MERCHANDISE_CITY] ?? ''),
    regionOrStateCode: String(personalDetails[AddressFormKey.MERCHANDISE_STATE_CODE] ?? ''),
    postalCode: String(personalDetails[AddressFormKey.MERCHANDISE_POSTAL_CODE] ?? ''),
    streetNumber: String(personalDetails[AddressFormKey.MERCHANDISE_STREET_NUMBER] ?? ''),
    street: String(personalDetails[AddressFormKey.MERCHANDISE_STREET] ?? ''),
    poBox: String(personalDetails[AddressFormKey.MERCHANDISE_PO_BOX] ?? ''),
  };
}
